import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { twoFactorAPI } from '../lib/api';
import { Shield, Smartphone, CheckCircle2, AlertTriangle, Loader2, QrCode, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function SecuritySettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ enabled: false });
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    loadStatus();
  }, [user, authLoading, navigate]);

  const loadStatus = async () => {
    try {
      const res = await twoFactorAPI.status();
      setStatus(res.data);
    } catch (err) {
      toast.error('Failed to load 2FA status');
    }
    setLoading(false);
  };

  const handleSetup = async () => {
    setSubmitting(true);
    try {
      const res = await twoFactorAPI.setup();
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start 2FA setup');
    }
    setSubmitting(false);
  };

  const handleEnable = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setSubmitting(true);
    try {
      await twoFactorAPI.enable(verifyCode);
      toast.success('Two-factor authentication enabled!');
      setSetupData(null);
      setVerifyCode('');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    }
    setSubmitting(false);
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setSubmitting(true);
    try {
      await twoFactorAPI.disable(disableCode);
      toast.success('Two-factor authentication disabled');
      setDisableCode('');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    }
    setSubmitting(false);
  };

  if (loading || authLoading) {
    return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;
  }

  return (
    <div className="px-6 md:px-12 max-w-3xl mx-auto py-12 md:py-20" data-testid="security-settings-page">
      <div className="flex items-center gap-3 mb-10">
        <Shield className="h-6 w-6 text-[#00E5FF]" />
        <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight">Security Settings</h1>
      </div>

      {/* 2FA Status Card */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Smartphone className="h-5 w-5 text-[#00E5FF]" />
            <h2 className="text-lg font-bold text-white">Two-Factor Authentication</h2>
          </div>
          <span className={`px-3 py-1 text-xs font-bold rounded-sm ${status.enabled ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
            {status.enabled ? 'ENABLED' : 'DISABLED'}
          </span>
        </div>
        <p className="text-sm text-zinc-500 mb-6">
          Add an extra layer of security to your account by requiring a verification code from your authenticator app when signing in.
        </p>

        {status.enabled ? (
          /* Disable 2FA */
          <div className="space-y-4">
            <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-sm flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-400">2FA is active</p>
                <p className="text-xs text-zinc-500 mt-0.5">Enabled on {status.enabled_at ? new Date(status.enabled_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
            
            <div className="pt-4 border-t border-white/5">
              <h3 className="text-sm font-bold text-white mb-3">Disable Two-Factor Authentication</h3>
              <p className="text-xs text-zinc-500 mb-3">Enter a code from your authenticator app to disable 2FA.</p>
              <div className="flex gap-3">
                <Input
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  className="w-40 bg-[#050505] border-zinc-800 text-white rounded-sm text-center tracking-widest"
                  maxLength={6}
                  data-testid="disable-2fa-code"
                />
                <Button onClick={handleDisable} disabled={submitting} variant="outline"
                  className="border-red-500/30 text-red-400 hover:border-red-500 rounded-sm" data-testid="disable-2fa-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable 2FA'}
                </Button>
              </div>
            </div>
          </div>
        ) : setupData ? (
          /* Setup Flow */
          <div className="space-y-6">
            <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-400">Setup in progress</span>
              </div>
              <p className="text-xs text-zinc-500">Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
            </div>

            <div className="flex flex-col md:flex-row gap-6 items-center">
              {/* QR Code */}
              <div className="bg-white p-4 rounded-sm">
                <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="qr-code-img" />
              </div>

              {/* Manual Entry */}
              <div className="flex-1 space-y-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Manual Entry Key</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Key className="h-4 w-4 text-zinc-600" />
                    <code className="text-sm text-[#00E5FF] font-mono bg-[#050505] px-3 py-1.5 rounded-sm border border-white/5">
                      {setupData.secret}
                    </code>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">If you can't scan the QR code, enter this key manually</p>
                </div>

                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Verification Code</Label>
                  <div className="mt-1 flex gap-3">
                    <Input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="w-40 bg-[#050505] border-zinc-800 text-white rounded-sm text-center tracking-widest"
                      maxLength={6}
                      data-testid="verify-2fa-code"
                    />
                    <Button onClick={handleEnable} disabled={submitting}
                      className="bg-[#00E5FF] text-black hover:bg-white font-bold rounded-sm" data-testid="enable-2fa-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enable 2FA'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={() => setSetupData(null)} variant="outline" className="border-zinc-800 text-zinc-400 rounded-sm">
              Cancel Setup
            </Button>
          </div>
        ) : (
          /* Enable Button */
          <div>
            <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-sm mb-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-400">2FA is not enabled</p>
                <p className="text-xs text-zinc-500 mt-0.5">Your account is less secure without two-factor authentication</p>
              </div>
            </div>
            <Button onClick={handleSetup} disabled={submitting}
              className="bg-[#00E5FF] text-black hover:bg-white font-bold rounded-sm" data-testid="setup-2fa-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <QrCode className="h-4 w-4 mr-2" />}
              Set Up Two-Factor Authentication
            </Button>
          </div>
        )}
      </div>

      {/* Additional Security Info */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Authenticator Apps</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: 'Google Authenticator', platform: 'iOS & Android' },
            { name: 'Authy', platform: 'iOS, Android & Desktop' },
            { name: 'Microsoft Authenticator', platform: 'iOS & Android' },
            { name: '1Password', platform: 'All platforms' },
          ].map((app, i) => (
            <div key={i} className="p-3 bg-[#050505] border border-white/5 rounded-sm">
              <div className="text-sm text-white font-medium">{app.name}</div>
              <div className="text-xs text-zinc-600">{app.platform}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
